import { useState, useEffect, useRef, Fragment } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

import { toast } from "sonner";
import { trpc } from "../lib/trpc";
import { Plus, Trash2, Copy, FileDown, FileUp, FileSpreadsheet, Search } from "lucide-react";
import * as XLSX from 'xlsx';
import { SelectPaymentTermModal } from "./SelectPaymentTermModal";
import { EtiquetasEditor } from "@/components/Etiquetas";

interface EditQuoteModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orcamentoId: number;
  readOnly?: boolean;
}

interface QuoteItem {
  tempId: string;
  productId: number;
  codigo: string;
  descricao: string;
  quantidade: string;
  valorVenda: string;
  valorTotalVenda: string;
  estoque: string;
}

export function EditQuoteModal({ open, onClose, onSuccess, orcamentoId, readOnly = false }: EditQuoteModalProps) {
  console.log('[EditQuoteModal] COMPONENTE RENDERIZADO');
  console.log('[EditQuoteModal] Props - open:', open);
  console.log('[EditQuoteModal] Props - orcamentoId:', orcamentoId);
  const { user } = useGoogleAuth();
  const utils = trpc.useUtils();
  const itensCarregadosParaOrcamentoId = useRef<number | null>(null);
  
  // Estados do formulário - Cliente
  const [destinatarioId, setDestinatarioId] = useState("");
  const [destinatarioNome, setDestinatarioNome] = useState("");
  const [isDestinatarioModalOpen, setIsDestinatarioModalOpen] = useState(false);
  const [destinatarioSearch, setDestinatarioSearch] = useState("");
  const [destinatarioPage, setDestinatarioPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  
  // Comercial
  const [quoteConfigId, setQuoteConfigId] = useState("");
  const [quoteConfigNome, setQuoteConfigNome] = useState("");
  const [isQuoteConfigModalOpen, setIsQuoteConfigModalOpen] = useState(false);
  const [quoteConfigSearch, setQuoteConfigSearch] = useState("");
  const [quoteConfigPage, setQuoteConfigPage] = useState(1);
  
  const [priceTableId, setPriceTableId] = useState("");
  const [priceTableNome, setPriceTableNome] = useState("");
  const [isPriceTableModalOpen, setIsPriceTableModalOpen] = useState(false);
  const [priceTableSearch, setPriceTableSearch] = useState("");
  const [priceTablePage, setPriceTablePage] = useState(1);
  
  // Condição de pagamento
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState<any>(null);
  const [isPaymentTermModalOpen, setIsPaymentTermModalOpen] = useState(false);
  
  // Logística
  const [transportadora, setTransportadora] = useState("");
  const [transportadoraId, setTransportadoraId] = useState<number | null>(null);
  const [isTransportadoraModalOpen, setIsTransportadoraModalOpen] = useState(false);
  const [transportadoraSearch, setTransportadoraSearch] = useState("");
  const [tipoFrete, setTipoFrete] = useState("");
  const [tipoEntrega, setTipoEntrega] = useState("");
  
  // Endereço de entrega
  const [cep, setCep] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  
  // Observações (inline)
  const [obsPedidoInterno, setObsPedidoInterno] = useState("");
  const [obsPedidoDestinatario, setObsPedidoDestinatario] = useState("");
  
  // Itens do orçamento
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [itemsRevision, setItemsRevision] = useState<string | null>(null);
  const [itemsProntosParaSalvar, setItemsProntosParaSalvar] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [itemQuantidade, setItemQuantidade] = useState("");
  const [selectedProductDescription, setSelectedProductDescription] = useState("");
  const [selectedProductPrice, setSelectedProductPrice] = useState("");
  const [selectedProductMinPrice, setSelectedProductMinPrice] = useState<number>(0);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [isImportErrorsModalOpen, setIsImportErrorsModalOpen] = useState(false);
  
  // Queries
  const { data: quoteConfigs = [] } = trpc.quoteTypes.list.useQuery();
  const quoteConfigSelecionada = Array.isArray(quoteConfigs)
    ? quoteConfigs.find((config: any) => config.id === parseInt(quoteConfigId))
    : null;
  const emitenteIdConfiguracao = Number(quoteConfigSelecionada?.emitenteId || 0);
  const filtroEmitenteConfiguracao = emitenteIdConfiguracao > 0
    ? { emitenteId: emitenteIdConfiguracao }
    : undefined;
  const { data: destinatariosResp } = trpc.destinatarios.list.useQuery(
    {
      ...filtroEmitenteConfiguracao,
      page: destinatarioPage,
      limit: ITEMS_PER_PAGE,
      search: destinatarioSearch || undefined,
    },
    { enabled: !!user && emitenteIdConfiguracao > 0 }
  );
  const destinatariosList = destinatariosResp?.data ?? [];
  const destinatariosTotal = destinatariosResp?.total ?? 0;
  const destinatariosTotalPages = destinatariosResp?.totalPages ?? 0;
  const { data: transportadorasResp } = trpc.transportadoras.list.useQuery(
    filtroEmitenteConfiguracao ? { ...filtroEmitenteConfiguracao, all: true } : undefined,
    { enabled: !!user && emitenteIdConfiguracao > 0 }
  );
  const transportadorasList = (transportadorasResp as any)?.data ?? transportadorasResp ?? [];
  const transportadoraIdAtual = Number(transportadora);
  const { data: transportadoraAtual } = trpc.transportadoras.getById.useQuery(
    { id: transportadoraIdAtual },
    { enabled: Number.isInteger(transportadoraIdAtual) && transportadoraIdAtual > 0 }
  );
  const { data: priceTablesResp } = trpc.priceTables.list.useQuery(
    filtroEmitenteConfiguracao,
    { enabled: !!user && emitenteIdConfiguracao > 0 }
  );
  const priceTablesList = Array.isArray(priceTablesResp) ? priceTablesResp : [];
  
  // Query de itens da tabela de preço selecionada
  const { data: priceTableItems = [] } = trpc.priceTables.getItems.useQuery(
    { priceTableId: parseInt(priceTableId) },
    { enabled: !!priceTableId }
  );
  const priceTablesDoEmitenteConfiguracao = emitenteIdConfiguracao > 0
    ? priceTablesList.filter((priceTable: any) => Number(priceTable.emitenteId) === emitenteIdConfiguracao)
    : [];
  const priceTableItemsDoEmitenteConfiguracao = emitenteIdConfiguracao > 0
    ? (Array.isArray(priceTableItems) ? priceTableItems.filter((priceItem: any) => Number(priceItem.emitenteId) === emitenteIdConfiguracao) : [])
    : [];
  
  // Preview de impostos (ST unitária + totais)
  const { data: impostosPreview } = trpc.orcamentos.impostosPreview.useQuery(
    { orcamentoId },
    { enabled: !!orcamentoId && open }
  );
  const impostosPreviewRelatorioMutation = trpc.orcamentos.impostosPreviewRelatorio.useMutation();

  // NOVOS hooks para edição
  const { data: orcamentoEditData } = trpc.orcamentos.fetchOrcamentoForEdit.useQuery(
    { orcamentoId },
    { enabled: !!orcamentoId && open }
  );
  console.log('[EditQuoteModal] orcamentoId:', orcamentoId);
  console.log('[EditQuoteModal] open:', open);
  console.log('[EditQuoteModal] query enabled:', !!orcamentoId && open);
  console.log('[EditQuoteModal] orcamentoEditData:', orcamentoEditData);
  
  const { refetch: refetchItemsForEdit } = trpc.orcamentos.fetchItemsForEdit.useQuery(
    { orcamentoId },
    { enabled: false }
  );
  
  // Buscar condição de pagamento quando carregar orçamento
  const { data: paymentTermData } = trpc.paymentTerms.getById.useQuery(
    { id: orcamentoEditData?.condicaoPagamentoId || 0 },
    { enabled: !!orcamentoEditData?.condicaoPagamentoId }
  );
  const { data: destinatarioDetalhes } = trpc.destinatarios.getById.useQuery(
    { id: parseInt(destinatarioId) },
    { enabled: !!destinatarioId }
  );
  const observacoesDoDestinatario = [
    { titulo: "Observações", valor: destinatarioDetalhes?.observacoes },
    { titulo: "Observações Comerciais", valor: destinatarioDetalhes?.observacoesComerciais },
    { titulo: "Anotações", valor: destinatarioDetalhes?.anotacoes },
  ].filter(({ valor }) => String(valor ?? "").trim()).map(({ titulo, valor }) => `${titulo}:\n${String(valor).trim()}`).join("\n\n") || "Sem observações cadastradas";
  
  // Log quando items são carregados
  useEffect(() => {
    if (priceTableItems.length > 0) {
      console.log('priceTableItems loaded', priceTableItems.length);
    }
  }, [priceTableItems.length]);
  
  // Query de estoque do emitente da configuração de orçamento
  const { data: stockData } = trpc.products.list.useQuery(
    filtroEmitenteConfiguracao,
    { enabled: !!user && emitenteIdConfiguracao > 0 }
  );
  const isStockLoading = stockData === undefined;
  const getDynamicStock = (productId: number | string) => {
    const stock = ((stockData ?? []) as any[]).find((item: any) => Number(item.id) === Number(productId));
    return stock?.quantidade ?? 0;
  };
  
  // Carregar dados do destinatário quando selecionado
  useEffect(() => {
    if (destinatarioId) {
      const dest = destinatariosList.find((d: any) => d.id === parseInt(destinatarioId));
      if (dest) {
        // Endereço
        setCep(dest.cep || "");
        setLogradouro(dest.logradouro || "");
        setNumero(dest.numero || "");
        setComplemento(dest.complemento || "");
        setBairro(dest.bairro || "");
        setCidade(dest.cidade || "");
        setUf(dest.uf || "");
        
        // Transportadora
        setTransportadora(dest.transportadora || "");
      }
    }
  }, [destinatarioId, destinatariosList]);
  
  // NOVO: Popular campos quando orçamento for carregado
  useEffect(() => {
    console.log('[EditQuoteModal] useEffect população EXECUTOU');
    console.log('[EditQuoteModal] useEffect - orcamentoEditData:', orcamentoEditData);
    console.log('[EditQuoteModal] useEffect - orcamentoId:', orcamentoId);
    if (orcamentoEditData && orcamentoId) {
      console.log('[EditQuoteModal] useEffect - DENTRO DO IF, vai popular campos');
      setDestinatarioId(String(orcamentoEditData.destinatarioId || ''));
      setQuoteConfigId(String(orcamentoEditData.tipoOrcamentoId || ''));
      setPriceTableId(String(orcamentoEditData.tabelaPrecoId || ''));
      // Condição de pagamento será carregada via query separada
      setTipoFrete(orcamentoEditData.tipoFrete || '');
      setTipoEntrega(orcamentoEditData.tipoEntrega || '');
      setTransportadora(orcamentoEditData.transportadora || '');
      setObsPedidoInterno(orcamentoEditData.obsPedidoInterno || '');
      setObsPedidoDestinatario(orcamentoEditData.obsPedidoDestinatario || '');
      
      // Popular nomes dos lookups
      setDestinatarioNome(orcamentoEditData.destinatarioNome || '');
      setPriceTableNome(orcamentoEditData.tabelaPrecoNome || '');
      setQuoteConfigNome(orcamentoEditData.tipoOrcamentoNome || '');
      
      // Parse endereço de entrega se existir
      if (orcamentoEditData.enderecoEntrega) {
        try {
          const endereco = typeof orcamentoEditData.enderecoEntrega === 'string'
            ? JSON.parse(orcamentoEditData.enderecoEntrega)
            : orcamentoEditData.enderecoEntrega;
          
          setCep(endereco.cep || '');
          setLogradouro(endereco.logradouro || '');
          setNumero(endereco.numero || '');
          setComplemento(endereco.complemento || '');
          setBairro(endereco.bairro || '');
          setCidade(endereco.cidade || '');
          setUf(endereco.uf || '');
        } catch (e) {
          console.error('Erro ao parsear endereço:', e);
        }
      }
      console.log('[EditQuoteModal] useEffect - CAMPOS POPULADOS COM SUCESSO');
    } else {
      console.log('[EditQuoteModal] useEffect - NÃO ENTROU NO IF (dados não existem)');
    }
  }, [orcamentoEditData, orcamentoId]);
  
  // Carregar condição de pagamento quando dados estiverem disponíveis
  useEffect(() => {
    if (paymentTermData) {
      setSelectedPaymentTerm(paymentTermData);
    }
  }, [paymentTermData]);
  
  // Carregar os itens atuais do banco a cada abertura, sem usar a lista em cache.
  useEffect(() => {
    if (!open || !orcamentoId) return;

    let ativo = true;
    itensCarregadosParaOrcamentoId.current = null;
    setItems([]);
    setItemsRevision(null);
    setItemsProntosParaSalvar(false);

    void refetchItemsForEdit().then(({ data }) => {
      if (!ativo || !data) return;
      const loadedItems: QuoteItem[] = data.items.map((item: any) => ({
        tempId: String(item.id),
        productId: item.produtoId || 0,
        codigo: item.produtoCod || '',
        descricao: item.produtoDescricao || '',
        quantidade: String(item.quantidade || 0),
        valorVenda: String(Number(item.valorUnitario || 0).toFixed(2)),
        valorTotalVenda: String(Number(item.valorTotal || 0).toFixed(2)),
        estoque: isStockLoading ? "Carregando" : String(getDynamicStock(item.produtoId)),
      }));
      setItems(loadedItems);
      setItemsRevision(data.itemsRevision);
      setItemsProntosParaSalvar(true);
      itensCarregadosParaOrcamentoId.current = orcamentoId;
    });

    return () => {
      ativo = false;
    };
  }, [open, orcamentoId, refetchItemsForEdit]);

  useEffect(() => {
    if (isStockLoading) return;
    setItems((itensAtuais) => itensAtuais.map((item) => ({
      ...item,
      estoque: String(getDynamicStock(item.productId)),
    })));
  }, [stockData]);

  // Mutation
  const createMutation = trpc.orcamentos.create.useMutation({
    onSuccess: () => {
      toast.success("Orçamento criado com sucesso");
      handleClose();
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar orçamento");
    },
  });

  const updateMutation = trpc.orcamentos.update.useMutation({
    onSuccess: () => {
      toast.success("Orçamento atualizado com sucesso");
      handleClose();
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar orçamento");
    },
  });
  
  const handleClose = () => {
    // Limpar formulário
    setDestinatarioId("");
    setDestinatarioSearch("");
    setQuoteConfigId("");
    setQuoteConfigSearch("");
    setPriceTableId("");
    setPriceTableSearch("");
    setSelectedPaymentTerm(null);
    setTransportadora("");
    setTransportadoraId(null);
    setTransportadoraSearch("");
    setTipoFrete("");
    setTipoEntrega("");
    setCep("");
    setLogradouro("");
    setNumero("");
    setComplemento("");
    setBairro("");
    setCidade("");
    setUf("");
    setObsPedidoInterno("");
    setObsPedidoDestinatario("");
    setItems([]);
    setItemsRevision(null);
    setItemsProntosParaSalvar(false);
    itensCarregadosParaOrcamentoId.current = null;
    setSearchProduct("");
    setItemQuantidade("");
    
    onClose();
  };
  
  const handleProductLookup = (typedValue?: string) => {
    const typed = (typedValue || searchProduct).trim();
    console.log('handleProductLookup called', { priceTableId, typed, itemsCount: priceTableItems.length });
    
    if (!priceTableId) {
      toast.error("Selecione uma tabela de preço primeiro");
      return;
    }
    if (!typed) {
      return;
    }
    
    // Priorizar código exato antes da busca por descrição
    const typedLower = typed.toLowerCase();
    const priceItem = priceTableItemsDoEmitenteConfiguracao.find((pi: any) =>
      (pi.codigo ?? '').toString().toLowerCase() === typedLower
    ) ?? priceTableItemsDoEmitenteConfiguracao.find((pi: any) =>
      (pi.descricao ?? '').toString().toLowerCase().includes(typedLower)
    );
    
    if (!priceItem) {
      toast.error("Produto não encontrado na tabela de preço selecionada");
      setSelectedProductDescription("");
      setSelectedProductPrice("");
      return;
    }
    
    const valorVenda = Number(priceItem.valorComImpostos || priceItem.valor || 0);
    const produtoVenda = Number(priceItem.produtoVenda || 0);
    const variacaoMax = user?.variacaoMaxVenda ?? null;
    const minPrice = (variacaoMax !== null && user?.role !== 'admin') ? produtoVenda * (1 - variacaoMax / 100) : 0;
    setSelectedProductDescription(priceItem.descricao);
    setSelectedProductPrice(valorVenda.toFixed(2));
    setSelectedProductMinPrice(minPrice);
    setShowProductDropdown(false);
    
    // Focar no campo quantidade
    setTimeout(() => {
      const qtdInput = document.querySelector('input[placeholder*="Quantidade"]') as HTMLInputElement;
      if (qtdInput) qtdInput.focus();
    }, 100);
  };
  
  const handleAddItem = () => {
    // Validação: precisa ter destinatário, tabela e condição de pagamento
    if (!destinatarioId) {
      toast.error("Selecione um destinatário primeiro");
      return;
    }
    if (!priceTableId) {
      toast.error("Selecione uma tabela de preço primeiro");
      return;
    }
    if (!selectedPaymentTerm) {
      toast.error("Selecione a condição de pagamento primeiro");
      return;
    }
    
    if (!searchProduct) {
      toast.error("Selecione um produto");
      return;
    }
    if (!itemQuantidade || parseFloat(itemQuantidade) <= 0) {
      toast.error("Informe uma quantidade válida");
      return;
    }
    
    // Priorizar código exato antes da busca por descrição
    const searchLower = searchProduct.trim().toLowerCase();
    const priceItem = priceTableItemsDoEmitenteConfiguracao.find((pi: any) =>
      (pi.codigo ?? '').toString().toLowerCase() === searchLower
    ) ?? priceTableItemsDoEmitenteConfiguracao.find((pi: any) =>
      (pi.descricao ?? '').toString().toLowerCase().includes(searchLower)
    );
    
    if (!priceItem) {
      toast.error("Produto não encontrado na tabela de preço selecionada");
      return;
    }
    
    const valorVenda = Number(selectedProductPrice || priceItem.valorComImpostos || priceItem.valor || 0);
    
    // Validar variação máxima de venda
    const produtoVenda = Number(priceItem.produtoVenda || 0);
    const variacaoMax = user?.variacaoMaxVenda ?? null;
    if (variacaoMax !== null && user?.role !== 'admin' && produtoVenda > 0) {
      const minPrice = produtoVenda * (1 - variacaoMax / 100);
      if (valorVenda < minPrice) {
        toast.error(`Valor R$ ${valorVenda.toFixed(2)} abaixo do mínimo permitido R$ ${minPrice.toFixed(2)}`);
        return;
      }
    }
    
    const quantidade = parseFloat(itemQuantidade);
    const valorTotal = Number(valorVenda * quantidade);
    
    // Saldo dinâmico do mesmo produto e emitente
    const estoqueQtd = getDynamicStock(priceItem.productId);
    
    const newItem: QuoteItem = {
      tempId: Date.now().toString(),
      productId: priceItem.productId,
      codigo: priceItem.codigo,
      descricao: priceItem.descricao,
      quantidade: itemQuantidade,
      valorVenda: valorVenda.toFixed(2),
      valorTotalVenda: valorTotal.toFixed(2),
      estoque: estoqueQtd.toString(),
    };
    
    setItems((itensAtuais) => [...itensAtuais, newItem]);
    setSearchProduct("");
    setSelectedProductDescription("");
    setSelectedProductPrice("");
    setItemQuantidade("");
  };
  
  const handleRemoveItem = (tempId: string) => {
    setItems((itensAtuais) => itensAtuais.filter(item => item.tempId !== tempId));
  };
  
  const handleAlterarQuantidadeItem = (tempId: string, quantidade: string) => {
    const quantidadeNumerica = Number(quantidade);
    setItems((itensAtuais) => itensAtuais.map((item) => {
      if (item.tempId !== tempId) return item;
      return {
        ...item,
        quantidade,
        valorTotalVenda: Number.isFinite(quantidadeNumerica) && quantidadeNumerica > 0
          ? (Number(item.valorVenda) * quantidadeNumerica).toFixed(2)
          : '0.00',
      };
    }));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + parseFloat(item.valorTotalVenda), 0).toFixed(2);
  };
  
  const handleDuplicar = () => {
    toast.info("Função duplicar orçamento em desenvolvimento");
  };
  
  const handleRelatorio = async (
    format: 'excel' | 'pdf',
    opcoes?: {
      itens: QuoteItem[];
      titulo: string;
      arquivo: string;
    },
  ) => {
    const itensRelatorio = opcoes?.itens ?? items;
    let impostosAtualizados;
    try {
      impostosAtualizados = await impostosPreviewRelatorioMutation.mutateAsync({
        orcamentoId,
        itensRelatorio: itensRelatorio.map((item) => ({
          produtoId: item.productId,
          quantidade: Number(item.quantidade) || 0,
          valorUnitario: Number(item.valorVenda) || 0,
          valorTotal: Number(item.valorTotalVenda) || 0,
        })),
      });
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível recalcular os impostos do relatório.');
      return;
    }
    const impostoDoItem = (item: QuoteItem, indice: number) => impostosAtualizados.itens[indice] ?? impostosAtualizados.itens.find((imposto: any) => imposto.produtoId === item.productId);
    const totalSemImposto = itensRelatorio.reduce((sum, item) => sum + (Number(item.valorTotalVenda) || 0), 0);
    const totalSt = itensRelatorio.reduce((sum, item, indice) => sum + ((Number(impostoDoItem(item, indice)?.stUnitaria) || 0) * (Number(item.quantidade) || 0)), 0);
    const totalIpi = itensRelatorio.reduce((sum, item, indice) => sum + ((Number(impostoDoItem(item, indice)?.ipiUnitaria) || 0) * (Number(item.quantidade) || 0)), 0);
    const totalComImposto = totalSemImposto + totalSt + totalIpi;
    const arquivoRelatorio = opcoes?.arquivo ?? `relatorio_orcamento_${orcamentoId}`;
    const transportadoraRelatorio = (() => {
      const tId = parseInt(transportadora);
      return !isNaN(tId) ? (transportadorasList.find((t: any) => t.id === tId)?.razaoSocial || transportadoraAtual?.razaoSocial || transportadora) : transportadora;
    })();
    const dadosOrcamento = [
      ['Orçamento', String(orcamentoId)],
      ['Destinatário', destinatarioNome],
      ['Configuração', quoteConfigNome],
      ['Tabela de Preço', priceTableNome],
      ['Condição de Pagamento', selectedPaymentTerm?.nome || ''],
      ['Tipo de Frete', tipoFrete],
      ['Tipo de Entrega', tipoEntrega],
      ['Transportadora', transportadoraRelatorio],
      ['CEP', cep],
      ['Logradouro', logradouro],
      ['Número', numero],
      ['Complemento', complemento],
      ['Bairro', bairro],
      ['Cidade', cidade],
      ['UF', uf],
      ['Obs. Pedido Interno', obsPedidoInterno],
      ['Obs. Pedido Destinatário', obsPedidoDestinatario],
      ['Total sem imposto', `R$ ${totalSemImposto.toFixed(2)}`],
      ['Total ST', `R$ ${totalSt.toFixed(2)}`],
      ['Total IPI', `R$ ${totalIpi.toFixed(2)}`],
      ['Total com imposto', `R$ ${totalComImposto.toFixed(2)}`],
    ];
    const dadosItens = itensRelatorio.map((item, indice) => {
      const imposto = impostoDoItem(item, indice);
      const stUnit = Number(imposto?.stUnitaria) || 0;
      const ipiUnit = Number(imposto?.ipiUnitaria) || 0;
      const valorUnitario = Number(item.valorVenda) || 0;
      const quantidade = Number(item.quantidade) || 0;
      return {
        'Código': item.codigo,
        'Descrição': item.descricao,
        'Quantidade': quantidade,
        'Valor Unit. sem imposto': valorUnitario,
        'ST Unit.': stUnit,
        'IPI Unit.': ipiUnit,
        'Valor com imposto': valorUnitario + stUnit + ipiUnit,
        'Valor total': (valorUnitario + stUnit + ipiUnit) * quantidade,
      };
    });

    if (format === 'excel') {
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([['Campo', 'Valor'], ...dadosOrcamento]), 'Orçamento');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dadosItens), 'Itens');
      XLSX.writeFile(workbook, `${arquivoRelatorio}.xlsx`);
      toast.success(opcoes ? 'Relatório disponível Excel exportado' : 'Relatório Excel exportado');
      return;
    }

    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageMargin = 14;
    const accentColor: [number, number, number] = [34, 96, 167];
    const formatCurrency = (value: unknown) => {
      const amount = Number(value);
      return `R$ ${(Number.isFinite(amount) ? amount : 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };
    const formatCnpj = (value: unknown) => {
      const digits = String(value || '').replace(/\D/g, '');
      return digits.length === 14
        ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
        : String(value || '-');
    };
    const formatCep = (value: string) => {
      const digits = value.replace(/\D/g, '');
      return digits.length === 8 ? digits.replace(/^(\d{5})(\d{3})$/, '$1-$2') : value;
    };
    const formatPrazo = (value: unknown) => {
      const prazo = String(value || '').trim();
      return prazo && /\d/.test(prazo) && !/dia/i.test(prazo) ? `${prazo} dias` : prazo || '-';
    };
    const formatQuantidade = (value: unknown) => {
      const quantidade = Number(value);
      return Number.isFinite(quantidade)
        ? quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 4 })
        : '0';
    };
    const destinatarioSelecionado = destinatariosList.find((destinatario: any) => destinatario.id === parseInt(destinatarioId));
    const enderecoEntrega = [
      [logradouro, numero].filter(Boolean).join(', '),
      complemento,
      bairro,
      [cidade, uf].filter(Boolean).join('/'),
      cep ? `CEP ${formatCep(cep)}` : '',
    ].filter(Boolean).join(' — ') || '-';
    const pdfItens = itensRelatorio.map((item, indice) => {
      const imposto = impostoDoItem(item, indice);
      const stUnit = Number(imposto?.stUnitaria) || 0;
      const ipiUnit = Number(imposto?.ipiUnitaria) || 0;
      const valorUnitario = Number(item.valorVenda) || 0;
      const quantidade = Number(item.quantidade) || 0;
      const valorComImposto = valorUnitario + stUnit + ipiUnit;
      return [
        item.codigo || '-',
        item.descricao || '-',
        formatQuantidade(item.quantidade),
        formatCurrency(valorUnitario),
        formatCurrency(stUnit),
        formatCurrency(ipiUnit),
        formatCurrency(valorComImposto),
        formatCurrency(valorComImposto * quantidade),
      ];
    });

    doc.setTextColor(...accentColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(opcoes?.titulo ?? 'RELATÓRIO DE ORÇAMENTO', pageWidth / 2, 14, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.text(`ORÇAMENTO Nº ${orcamentoEditData?.numero ?? orcamentoId}`, pageWidth / 2, 20, { align: 'center' });
    doc.setDrawColor(...accentColor);
    doc.setLineWidth(0.6);
    doc.line(pageMargin, 26, pageWidth - pageMargin, 26);

    const metaRight = pageWidth / 2 + 4;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('Emitente', pageMargin, 34);
    doc.text('Vendedor', metaRight, 34);
    doc.text('Cliente', pageMargin, 48);
    doc.text('CNPJ', metaRight, 48);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(quoteConfigSelecionada?.emitenteNome || '-', pageMargin, 40, { maxWidth: 128 });
    doc.text(destinatarioDetalhes?.vendedor || destinatarioSelecionado?.vendedor || '-', metaRight, 40, { maxWidth: 125 });
    doc.text(destinatarioNome || destinatarioDetalhes?.razaoSocial || '-', pageMargin, 54, { maxWidth: 128 });
    doc.text(formatCnpj(destinatarioDetalhes?.cnpj || destinatarioSelecionado?.cnpj), metaRight, 54, { maxWidth: 125 });

    const addressY = 60;
    const addressHeight = 20;
    doc.setFillColor(248, 248, 248);
    doc.setDrawColor(185, 185, 185);
    doc.setLineWidth(0.4);
    doc.roundedRect(pageMargin, addressY, pageWidth - (pageMargin * 2), addressHeight, 1.2, 1.2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('Endereço de Entrega', pageMargin + 3, addressY + 6);
    doc.text('Prazo', pageWidth - 50, addressY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(enderecoEntrega, pageMargin + 3, addressY + 13, { maxWidth: pageWidth - 88 });
    doc.text(formatPrazo(selectedPaymentTerm?.descricao), pageWidth - 50, addressY + 13, { maxWidth: 36 });

    autoTable(doc, {
      startY: 86,
      margin: { left: pageMargin, right: pageMargin },
      head: [['Código', 'Descrição', 'Qtd.', 'Vl. unit.\nsem imposto', 'ST\nunitária', 'IPI\nunitária', 'Vl. com\nimposto', 'Valor\ntotal']],
      body: pdfItens,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 1.6,
        lineColor: [185, 185, 185],
        lineWidth: 0.25,
        valign: 'middle',
      },
      headStyles: {
        fillColor: accentColor,
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 100 },
        2: { cellWidth: 13, halign: 'right' },
        3: { cellWidth: 27, halign: 'right' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 30, halign: 'right' },
        7: { cellWidth: 31, halign: 'right' },
      },
    });

    const totalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setTextColor(...accentColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Total sem imposto: ${formatCurrency(totalSemImposto)}`, pageWidth - pageMargin, totalY, { align: 'right' });
    doc.text(`Total ST: ${formatCurrency(totalSt)}`, pageWidth - pageMargin, totalY + 6, { align: 'right' });
    doc.text(`Total IPI: ${formatCurrency(totalIpi)}`, pageWidth - pageMargin, totalY + 12, { align: 'right' });
    doc.setFontSize(14);
    doc.text(`Total com imposto: ${formatCurrency(totalComImposto)}`, pageWidth - pageMargin, totalY + 20, { align: 'right' });
    doc.setTextColor(105, 105, 105);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('Campos fiscais sem valor são exibidos como R$ 0,00.', pageWidth - pageMargin, totalY + 26, { align: 'right' });
    doc.save(`${arquivoRelatorio}.pdf`);
    toast.success(opcoes ? 'Relatório disponível PDF exportado' : 'Relatório PDF exportado');
  };

  const handleRelatorioDisponivel = async (format: 'excel' | 'pdf') => {
    if (isStockLoading) {
      toast.info('Aguarde o carregamento do saldo dos produtos');
      return;
    }

    const quantidadePorProduto = new Map<number, number>();
    items.forEach((item) => {
      const produtoId = Number(item.productId);
      quantidadePorProduto.set(produtoId, (quantidadePorProduto.get(produtoId) ?? 0) + (Number(item.quantidade) || 0));
    });

    const itensDisponiveis = items.filter((item) => {
      const quantidadeSolicitada = quantidadePorProduto.get(Number(item.productId)) ?? 0;
      return Number(getDynamicStock(item.productId)) >= quantidadeSolicitada;
    });

    if (itensDisponiveis.length === 0) {
      toast.error('Nenhum item do orçamento possui saldo disponível');
      return;
    }

    await handleRelatorio(format, {
      itens: itensDisponiveis,
      titulo: 'RELATÓRIO DE ORÇAMENTO DISPONÍVEL',
      arquivo: `relatorio_orcamento_disponivel_${orcamentoId}`,
    });
  };
  
  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!quoteConfigId || !priceTableId) {
      toast.error('Selecione a configuração e a tabela de preço antes de importar');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        const importedItems: QuoteItem[] = [];
        const errors: string[] = [];

        (jsonData as any[]).forEach((row, index) => {
          const linha = index + 2;
          const codigoOriginal = row['Código'] ?? row['codigo'];
          const codigo = String(codigoOriginal ?? '').trim().replace(/^0+/, '');
          const quantidade = Number(String(row['Quantidade'] ?? row['quantidade'] ?? '').replace(',', '.'));
          const valorBruto = row['Valor'] ?? row['valor'];
          const temValorManual = valorBruto !== undefined && valorBruto !== null && String(valorBruto).trim() !== '';
          const valorManual = temValorManual ? Number(String(valorBruto).replace(',', '.')) : null;

          if (!codigo) {
            errors.push(`Linha ${linha}: código obrigatório após remover zeros à esquerda.`);
            return;
          }
          if (!Number.isFinite(quantidade) || quantidade <= 0) {
            errors.push(`Linha ${linha} — ${codigo}: quantidade deve ser maior que zero.`);
            return;
          }
          if (temValorManual && (!Number.isFinite(valorManual) || (valorManual as number) < 0)) {
            errors.push(`Linha ${linha} — ${codigo}: valor inválido.`);
            return;
          }

          const priceItem = priceTableItemsDoEmitenteConfiguracao.find((priceTableItem: any) => String(priceTableItem.codigo) === codigo);
          if (!priceItem) {
            errors.push(`Linha ${linha} — ${codigo}: produto não encontrado na tabela do emitente da configuração.`);
            return;
          }

          const valorVenda = valorManual ?? Number(priceItem.valorComImpostos || priceItem.valor || 0);
          const produtoVenda = Number(priceItem.produtoVenda || 0);
          const variacaoMax = user?.variacaoMaxVenda ?? null;
          if (variacaoMax !== null && user?.role !== 'admin' && produtoVenda > 0) {
            const minPrice = produtoVenda * (1 - variacaoMax / 100);
            if (valorVenda < minPrice) {
              errors.push(`Linha ${linha} — ${codigo}: valor R$ ${valorVenda.toFixed(2)} abaixo do mínimo R$ ${minPrice.toFixed(2)}.`);
              return;
            }
          }

          importedItems.push({
            tempId: `${Date.now()}-${linha}-${codigo}`,
            productId: priceItem.productId,
            codigo,
            descricao: priceItem.descricao,
            quantidade: quantidade.toString(),
            valorVenda: valorVenda.toFixed(2),
            valorTotalVenda: (valorVenda * quantidade).toFixed(2),
            estoque: String(getDynamicStock(priceItem.productId)),
          });
        });

        if (importedItems.length > 0) setItems((currentItems) => [...currentItems, ...importedItems]);
        if (errors.length > 0) {
          setImportErrors(errors);
          setIsImportErrorsModalOpen(true);
        }
        if (importedItems.length > 0) toast.success(`${importedItems.length} itens importados`);
      } catch {
        setImportErrors(['Não foi possível ler o arquivo Excel.']);
        setIsImportErrorsModalOpen(true);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };
  
  const handleExportExcel = () => {
    if (items.length === 0) {
      toast.error("Nenhum item para exportar");
      return;
    }
    
    const data = items.map(item => ({
      'Código': item.codigo,
      'Descrição': item.descricao,
      'Quantidade': parseFloat(item.quantidade),
      'Valor': parseFloat(item.valorVenda),
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Itens');
    XLSX.writeFile(workbook, 'orcamento_itens.xlsx');
    
    toast.success("Excel exportado");
  };
  
  const handleDownloadTemplate = () => {
    const data = [
      { 'Código': 'EXEMPLO001', 'Quantidade': 10, 'Valor': 100.00 },
      { 'Código': 'EXEMPLO002', 'Quantidade': 5, 'Valor': 50.00 },
    ];
    
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'template_importacao.xlsx');
    
    toast.success("Template baixado");
  };
  
  const handleSubmit = () => {
    // Validações
    if (!destinatarioId) {
      toast.error("Selecione um destinatário");
      return;
    }
    if (!quoteConfigId) {
      toast.error("Selecione uma configuração de orçamento");
      return;
    }
    if (!priceTableId) {
      toast.error("Selecione uma tabela de preço");
      return;
    }
    if (!selectedPaymentTerm) {
      toast.error("Selecione a condição de pagamento");
      return;
    }
    if (items.length === 0) {
      toast.error("Adicione pelo menos um item ao orçamento");
      return;
    }
    if (items.some((item) => !Number.isFinite(Number(item.quantidade)) || Number(item.quantidade) <= 0)) {
      toast.error("Informe uma quantidade válida para todos os itens");
      return;
    }
    if (!itemsProntosParaSalvar || !itemsRevision) {
      toast.error("Aguarde o carregamento atual dos itens antes de salvar");
      return;
    }
    
    // Montar endereço de entrega
    const enderecoEntrega = cep || logradouro || numero || bairro || cidade || uf
      ? {
          cep: cep || null,
          logradouro: logradouro || null,
          numero: numero || null,
          complemento: complemento || null,
          bairro: bairro || null,
          cidade: cidade || null,
          uf: uf || null,
        }
      : null;
    
    // Detectar se é criação ou edição
    if (orcamentoId && orcamentoId > 0) {
      // EDIÇÃO: atualizar orçamento existente
      updateMutation.mutate({
        id: orcamentoId,
        status: orcamentoEditData?.status === 'rascunho' ? 'pendente' : undefined,
        itemsRevision,
        destinatarioId: parseInt(destinatarioId),
        tipoOrcamentoId: parseInt(quoteConfigId),
        tabelaPrecoId: parseInt(priceTableId),
        condicaoPagamentoId: selectedPaymentTerm?.id,
        tipoFrete: tipoFrete || null,
        tipoEntrega: tipoEntrega || null,
        transportadora: transportadora || null,
        enderecoEntrega,
        obsPedidoInterno: obsPedidoInterno || null,
        obsPedidoDestinatario: obsPedidoDestinatario || null,
        valor: parseFloat(calculateTotal()),
        items: items.map(item => ({
          productId: item.productId,
          quantidade: parseFloat(item.quantidade),
          valorUnitario: parseFloat(item.valorVenda),
          valorTotal: parseFloat(item.valorTotalVenda),
        })),
      });
    } else {
      // CRIAÇÃO: criar novo orçamento
      createMutation.mutate({
        emitenteId: emitenteIdConfiguracao,
        destinatarioId: parseInt(destinatarioId),
        tipoOrcamentoId: parseInt(quoteConfigId),
        tabelaPrecoId: parseInt(priceTableId),
        condicaoPagamentoId: selectedPaymentTerm?.id,
        tipoFrete: tipoFrete || null,
        tipoEntrega: tipoEntrega || null,
        transportadora: transportadora || null,
        enderecoEntrega,
        obsPedidoInterno: obsPedidoInterno || null,
        obsPedidoDestinatario: obsPedidoDestinatario || null,
        valor: parseFloat(calculateTotal()),
        items: items.map(item => ({
          productId: item.productId,
          quantidade: parseFloat(item.quantidade),
          valorUnitario: parseFloat(item.valorVenda),
          valorTotal: parseFloat(item.valorTotalVenda),
        })),
      });
    }
  };
  
  // Filtros são aplicados diretamente no JSX
  
  // Filtrar configs de orçamento
  // filteredQuoteConfigs removido - usando modal de seleção
  

  
  // Filtrar produtos da tabela
  const filteredProducts = priceTableItemsDoEmitenteConfiguracao.filter((pi: any) =>
    (pi.codigo ?? '').toString().toLowerCase().includes(searchProduct.toLowerCase()) ||
    (pi.descricao ?? '').toString().toLowerCase().includes(searchProduct.toLowerCase())
  );
  
  // Bloquear tudo se não tiver destinatário ou se o orçamento estiver convertido
  const isBlocked = !destinatarioId || readOnly;
  const canAddItems = !readOnly && destinatarioId && quoteConfigId && priceTableId && selectedPaymentTerm;
  
  return (
    <Fragment>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[95vh] overflow-y-auto" style={{ width: '80%', maxWidth: '80%' }}>
        <DialogHeader>
          <DialogTitle>{readOnly ? "Visualizar Orçamento" : "Editar Orçamento"}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Cliente */}
          <div>
            <h3 className="font-semibold text-lg border-b pb-2 mb-4">Cliente</h3>
            <div>
              <Label>Destinatário *</Label>
              <div className="flex gap-2">
                <Input value={destinatarioNome} readOnly placeholder="Selecione um destinatário" />
                <Button
                  type="button"
                  onClick={() => setIsDestinatarioModalOpen(true)}
                  disabled={readOnly || !emitenteIdConfiguracao}
                  className={readOnly ? "hidden" : "bg-orange-500 hover:bg-orange-600"}
                >
                  Selecionar
                </Button>
              </div>
              <div className="mt-4">
                <Label>Observações do cliente</Label>
                <Textarea className="mt-1 resize-none" value={observacoesDoDestinatario} readOnly rows={6} />
              </div>
            </div>
          </div>
          
          {/* Comercial */}
          <div>
            <h3 className="font-semibold text-lg border-b pb-2 mb-4">Comercial</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Configuração de Orçamento *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Selecione uma configuração..."
                    value={quoteConfigNome}
                    readOnly
                    disabled={readOnly}
                  />
                  <Button
                    type="button"
                    onClick={() => setIsQuoteConfigModalOpen(true)}
                    disabled={readOnly}
                    className={readOnly ? "hidden" : "bg-orange-500 hover:bg-orange-600 whitespace-nowrap"}
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
              
              <div>
                <Label>Tabela de Preço * (Ativas do Emitente)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Selecione uma tabela de preço"
                    value={priceTableNome}
                    readOnly
                    disabled={isBlocked}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsPriceTableModalOpen(true)}
                    disabled={isBlocked || !emitenteIdConfiguracao}
                    className={readOnly ? "hidden" : "bg-orange-500 hover:bg-orange-600 text-white"}
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
              
              <div>
                <Label>Condição de Pagamento *</Label>
                <div className="flex gap-2">
                  <Input
                    value={selectedPaymentTerm ? `${selectedPaymentTerm.descricao} (${selectedPaymentTerm.quantidadeParcelas}x)` : ""}
                    placeholder="Nenhuma condição selecionada"
                    readOnly
                    disabled={isBlocked}
                  />
                  <Button
                    type="button"
                    onClick={() => setIsPaymentTermModalOpen(true)}
                    disabled={isBlocked || !emitenteIdConfiguracao}
                    className={readOnly ? "hidden" : "bg-orange-500 hover:bg-orange-600 whitespace-nowrap"}
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Logística */}
          <div>
            <h3 className="font-semibold text-lg border-b pb-2 mb-4">Logística</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Transportadora</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Selecione uma transportadora..."
                    value={(() => { const tId = parseInt(transportadora); return !isNaN(tId) ? (transportadorasList.find((t: any) => t.id === tId)?.razaoSocial || transportadoraAtual?.razaoSocial || transportadora) : transportadora; })()}
                    readOnly
                    disabled={isBlocked}
                  />
                  <Button
                    type="button"
                    onClick={() => setIsTransportadoraModalOpen(true)}
                    disabled={isBlocked || !emitenteIdConfiguracao}
                    className={readOnly ? "hidden" : "bg-orange-500 hover:bg-orange-600 whitespace-nowrap"}
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
              
              <div>
                <Label>Tipo de Frete</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={tipoFrete}
                  onChange={(e) => setTipoFrete(e.target.value)}
                  disabled={isBlocked}
                >
                  <option value="">Selecione...</option>
                  <option value="CIF">CIF</option>
                  <option value="FOB">FOB</option>
                  <option value="NOSSO_CARRO">NOSSO CARRO</option>
                  <option value="SEM_TRANSPORTE">SEM TRANSPORTE</option>
                </select>
              </div>
              
              <div>
                <Label>Tipo de Entrega</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={tipoEntrega}
                  onChange={(e) => setTipoEntrega(e.target.value)}
                  disabled={isBlocked}
                >
                  <option value="">Selecione...</option>
                  <option value="TRANSPORTADORA_COLETA">TRANSPORTADORA COLETA</option>
                  <option value="DESTINATARIO_COLETA">DESTINATARIO COLETA</option>
                  <option value="LEVAR_ATE_TRANSPORTADORA">LEVAR ATÉ A TRANSPORTADORA</option>
                  <option value="LEVAR_ATE_DESTINATARIO">LEVAR ATÉ O DESTINATARIO</option>
                  <option value="POR_APLICATIVO">POR APLICATIVO</option>
                  <option value="AGUARDAR_AGENDAMENTO">AGUARDAR AGENDAMENTO</option>
                  <option value="SEM_TRANSPORTE">SEM TRANSPORTE</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Endereço de Entrega */}
          <div>
            <h3 className="font-semibold text-lg border-b pb-2 mb-4">Endereço de Entrega (do Destinatário)</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>CEP</Label>
                <Input
                  value={cep}
                  onChange={(e) => setCep(e.target.value)}
                  disabled={isBlocked}
                />
              </div>
              
              <div className="md:col-span-2">
                <Label>Logradouro</Label>
                <Input
                  value={logradouro}
                  onChange={(e) => setLogradouro(e.target.value)}
                  disabled={isBlocked}
                />
              </div>
              
              <div>
                <Label>Número</Label>
                <Input
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  disabled={isBlocked}
                />
              </div>
              
              <div>
                <Label>Complemento</Label>
                <Input
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value)}
                  disabled={isBlocked}
                />
              </div>
              
              <div>
                <Label>Bairro</Label>
                <Input
                  value={bairro}
                  onChange={(e) => setBairro(e.target.value)}
                  disabled={isBlocked}
                />
              </div>
              
              <div>
                <Label>Cidade</Label>
                <Input
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  disabled={isBlocked}
                />
              </div>
              
              <div>
                <Label>UF</Label>
                <Input
                  maxLength={2}
                  value={uf}
                  onChange={(e) => setUf(e.target.value.toUpperCase())}
                  disabled={isBlocked}
                />
              </div>
            </div>
          </div>
          
          {/* Observações (Inline) */}
          <div>
            <h3 className="font-semibold text-lg border-b pb-2 mb-4">Observações</h3>
            <div className="space-y-3">
              <div>
                <Label>Obs. Pedido Interno (Edição Inline)</Label>
                <Input
                  value={obsPedidoInterno}
                  onChange={(e) => setObsPedidoInterno(e.target.value)}
                  placeholder="Texto livre..."
                  disabled={isBlocked}
                />
              </div>
              
              <div>
                <Label>Obs. Pedido Destinatário (Edição Inline)</Label>
                <Input
                  value={obsPedidoDestinatario}
                  onChange={(e) => setObsPedidoDestinatario(e.target.value)}
                  placeholder="Texto livre..."
                  disabled={isBlocked}
                />
              </div>
            </div>
          </div>
          
          {orcamentoId > 0 && (
            <EtiquetasEditor modulo="orcamento" entidadeId={orcamentoId} disabled={readOnly} />
          )}

          {/* Botões de Ação */}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleDuplicar} variant="outline" size="sm" disabled={isBlocked} className={readOnly ? "hidden" : ""}>
              <Copy className="w-4 h-4 mr-2" />
              Duplicar Orçamento
            </Button>
            <Button onClick={() => handleRelatorio('excel')} variant="outline" size="sm" disabled={isBlocked}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Relatório Excel
            </Button>
            <Button onClick={() => handleRelatorio('pdf')} variant="outline" size="sm" disabled={isBlocked}>
              <FileDown className="w-4 h-4 mr-2" />
              Relatório PDF
            </Button>
            <Button onClick={() => handleRelatorioDisponivel('excel')} variant="outline" size="sm" disabled={isBlocked || isStockLoading}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Relatório Disponível Excel
            </Button>
            <Button onClick={() => handleRelatorioDisponivel('pdf')} variant="outline" size="sm" disabled={isBlocked || isStockLoading}>
              <FileDown className="w-4 h-4 mr-2" />
              Relatório Disponível PDF
            </Button>
            <label>
              <Button type="button" onClick={() => document.getElementById('importar-excel-editar-orcamento')?.click()} variant="outline" size="sm" disabled={!canAddItems} className={readOnly ? "hidden" : ""}>
                <FileUp className="w-4 h-4 mr-2" />
                Importar Excel
              </Button>
              <input
                id="importar-excel-editar-orcamento"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleImportExcel}
                disabled={!canAddItems}
              />
            </label>
            <Button onClick={handleExportExcel} variant="outline" size="sm" disabled={items.length === 0}>
              <FileDown className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <Button onClick={handleDownloadTemplate} variant="outline" size="sm" className={readOnly ? "hidden" : ""}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Template Excel
            </Button>
          </div>
          
          {/* Itens do Orçamento */}
          <div>
            <h3 className="font-semibold text-lg border-b pb-2 mb-4">Itens do Orçamento</h3>
            
            {/* Adicionar item (sem dropdown, somente da tabela) */}
            {!readOnly && (
            <div className="bg-muted/50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-6 relative">
                  <Label>Produto (Somente da Tabela Selecionada)</Label>
                  <Input
                    placeholder="Buscar código ou descrição..."
                    value={searchProduct}
                    onChange={(e) => {
                      console.log("###NEWQUOTE_PRODUCT_ONCHANGE_MARKER_20260210###", e.target.value);
                      setSearchProduct(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const domValue = (e.target as HTMLInputElement).value;
                        console.log('onKeyDown', { key: e.key, domValue, stateValue: searchProduct, priceTableId, itemsCount: priceTableItems.length });
                        e.preventDefault();
                        handleProductLookup(domValue);
                      }
                    }}
                    disabled={!canAddItems}
                  />
                  {showProductDropdown && filteredProducts.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredProducts.slice(0, 10).map((p: any) => (
                        <div
                          key={p.id}
                          className="p-2 hover:bg-muted cursor-pointer"
                          onClick={() => {
                            setSearchProduct(p.codigo);
                            setShowProductDropdown(false);
                          }}
                        >
                          <div className="font-medium">{p.codigo}</div>
                          <div className="text-sm text-muted-foreground">{p.descricao}</div>
                          <div className="text-sm text-muted-foreground">
                            {isStockLoading ? "Saldo: carregando..." : `Saldo: ${Number(getDynamicStock(p.productId)).toFixed(0)}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="md:col-span-4">
                  <Label>Descrição</Label>
                  <Input
                    value={selectedProductDescription}
                    disabled
                    placeholder="Descrição do produto..."
                    className="bg-muted"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Preço Unit.</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedProductPrice}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (selectedProductMinPrice > 0 && val < selectedProductMinPrice) {
                        setSelectedProductPrice(selectedProductMinPrice.toFixed(2));
                      } else {
                        setSelectedProductPrice(e.target.value);
                      }
                    }}
                    disabled={!canAddItems}
                    placeholder="R$ 0,00"
                  />
                  {selectedProductMinPrice > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Mín: R$ {selectedProductMinPrice.toFixed(2)}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <Label>Quantidade</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={itemQuantidade}
                    onChange={(e) => setItemQuantidade(e.target.value)}
                    disabled={!canAddItems}
                  />
                </div>
                
                <div className="md:col-span-2 flex items-end">
                  <Button onClick={handleAddItem} disabled={!canAddItems} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Item
                  </Button>
                </div>
              </div>
            </div>
            )}
            
            {/* Lista de itens */}
            {items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 text-sm font-medium">Código</th>
                      <th className="text-left p-2 text-sm font-medium">Descrição</th>
                      <th className="text-right p-2 text-sm font-medium">Qtd</th>
                      <th className="text-right p-2 text-sm font-medium">Valor Venda</th>
                      <th className="text-right p-2 text-sm font-medium">ST Unit.</th>
                      <th className="text-right p-2 text-sm font-medium">Total Venda</th>
                      <th className="text-right p-2 text-sm font-medium">Estoque</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.tempId} className="border-t">
                        <td className="p-2 text-sm">{item.codigo}</td>
                        <td className="p-2 text-sm">{item.descricao}</td>
                        <td className="p-2 text-sm text-right">
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.quantidade}
                            onChange={(e) => handleAlterarQuantidadeItem(item.tempId, e.target.value)}
                            disabled={readOnly}
                            className="h-8 w-24 ml-auto text-right"
                          />
                        </td>
                        <td className="p-2 text-sm text-right">R$ {item.valorVenda}</td>
                        <td className="p-2 text-sm text-right text-muted-foreground">
                          {(() => {
                            const stInfo = impostosPreview?.itens?.find((i: any) => i.produtoId === item.productId);
                            if (!stInfo) return 'Erro de vínculo';
                            if (stInfo.stUnitaria != null) return `R$ ${stInfo.stUnitaria.toFixed(2)}`;
                            return stInfo.erro || '-';
                          })()}
                        </td>
                        <td className="p-2 text-sm text-right font-medium">R$ {item.valorTotalVenda}</td>
                        <td className="p-2 text-sm text-right">{item.estoque}</td>
                        <td className="p-2">
                          {!readOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(item.tempId)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t bg-muted/30">
                      <td colSpan={5} className="p-2 text-right font-semibold">Total do Orçamento:</td>
                      <td className="p-2 text-right font-bold text-lg">R$ {calculateTotal()}</td>
                      <td colSpan={2}></td>
                    </tr>
                    {impostosPreview && (
                      <>
                        <tr className="bg-muted/20">
                          <td colSpan={5} className="p-2 text-right text-sm font-medium text-muted-foreground">Total s/ ST:</td>
                          <td className="p-2 text-right text-sm font-medium">R$ {impostosPreview.totalSemSt.toFixed(2)}</td>
                          <td colSpan={2}></td>
                        </tr>
                        <tr className="bg-muted/20">
                          <td colSpan={5} className="p-2 text-right text-sm font-medium text-muted-foreground">Total c/ ST:</td>
                          <td className="p-2 text-right text-sm font-bold">R$ {impostosPreview.totalComSt.toFixed(2)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <div className="text-lg font-semibold">
            Total: R$ {calculateTotal()}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              {readOnly ? "Fechar" : "Cancelar"}
            </Button>
            {!readOnly && (
              <Button onClick={handleSubmit} disabled={updateMutation.isPending || !canAddItems || !itemsProntosParaSalvar || !itemsRevision}>
                {updateMutation.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={isImportErrorsModalOpen} onOpenChange={setIsImportErrorsModalOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Itens não importados</DialogTitle>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2 text-sm">
          {importErrors.map((error, index) => <p key={index}>{error}</p>)}
        </div>
        <div className="flex justify-end">
          <Button onClick={() => setIsImportErrorsModalOpen(false)}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de Seleção de Destinatário */}
    <Dialog open={isDestinatarioModalOpen} onOpenChange={setIsDestinatarioModalOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Destinatário</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Buscar por razão social, fantasia, CNPJ ou CPF..."
            value={destinatarioSearch}
            onChange={(e) => {
              setDestinatarioSearch(e.target.value);
              setDestinatarioPage(1);
            }}
          />
          <div className="border rounded-lg">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full border-collapse" style={{minWidth: "750px"}}>
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="border-b p-3 text-left" style={{minWidth: "200px"}}>Razão Social</th>
                    <th className="border-b p-3 text-left" style={{minWidth: "150px"}}>Fantasia</th>
                    <th className="border-b p-3 text-left" style={{minWidth: "200px"}}>Emitente</th>
                    <th className="border-b p-3 text-left" style={{minWidth: "140px"}}>CNPJ/CPF</th>
                    <th className="border-b p-3 text-center" style={{minWidth: "110px"}}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {destinatariosList.map((d: any) => (
                    <tr key={d.id} className="hover:bg-muted/50">
                      <td className="border-b p-3">{d.razaoSocial || '-'}</td>
                      <td className="border-b p-3">{d.nomeFantasia || '-'}</td>
                      <td className="border-b p-3">{d.emitenteNome || '-'}</td>
                      <td className="border-b p-3">{d.cnpj || d.cpf || '-'}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setDestinatarioId(String(d.id));
                            setDestinatarioNome(d.razaoSocial || d.nomeFantasia || `ID: ${d.id}`);
                            // Carregar dados do destinatário
                            if (d.transportadora) setTransportadora(d.transportadora);
                            if (d.entregaCep) setCep(d.entregaCep);
                            if (d.entregaLogradouro) setLogradouro(d.entregaLogradouro);
                            if (d.entregaNumero) setNumero(d.entregaNumero);
                            if (d.entregaComplemento) setComplemento(d.entregaComplemento);
                            if (d.entregaBairro) setBairro(d.entregaBairro);
                            if (d.entregaCidade) setCidade(d.entregaCidade);
                            if (d.entregaUf) setUf(d.entregaUf);
                            setIsDestinatarioModalOpen(false);
                            setDestinatarioSearch("");
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                    ))}
              </tbody>
            </table>
            </div>
            
            {/* Controles de paginação */}
            {destinatariosTotalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t bg-muted/30">
                <div className="text-sm text-muted-foreground">
                  Página {destinatarioPage} de {destinatariosTotalPages} ({destinatariosTotal} destinatários)
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDestinatarioPage(p => Math.max(1, p - 1))}
                    disabled={destinatarioPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDestinatarioPage(p => Math.min(destinatariosTotalPages, p + 1))}
                    disabled={destinatarioPage === destinatariosTotalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de Seleção de Tabela de Preço */}
    <Dialog open={isPriceTableModalOpen} onOpenChange={setIsPriceTableModalOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Tabela de Preço</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Buscar tabela..."
            value={priceTableSearch}
            onChange={(e) => {
              setPriceTableSearch(e.target.value);
              setPriceTablePage(1);
            }}
          />
          <div className="border rounded-lg">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full border-collapse" style={{minWidth: "500px"}}>
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="border-b p-3 text-left" style={{minWidth: "250px"}}>Nome</th>
                    <th className="border-b p-3 text-left" style={{minWidth: "80px"}}>ID</th>
                    <th className="border-b p-3 text-center" style={{minWidth: "110px"}}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = priceTablesDoEmitenteConfiguracao
                      .filter((pt: any) =>
                        priceTableSearch === "" ||
                        pt.nome?.toLowerCase().includes(priceTableSearch.toLowerCase())
                      );
                    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                    const startIndex = (priceTablePage - 1) * ITEMS_PER_PAGE;
                    const endIndex = startIndex + ITEMS_PER_PAGE;
                    const paginatedData = filtered.slice(startIndex, endIndex);
                    
                    return paginatedData.map((pt: any) => (
                    <tr key={pt.id} className="hover:bg-muted/50">
                      <td className="border-b p-3">{pt.nome || '-'}</td>
                      <td className="border-b p-3">{pt.id}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            const ptId = String(pt.id);
                            console.log("###NEWQUOTE_PRICETABLE_SELECT_MARKER_20260210###", pt.id);
                            setPriceTableId(ptId);
                            setPriceTableNome(pt.nome || `ID: ${pt.id}`);
                            setIsPriceTableModalOpen(false);
                            setPriceTableSearch("");
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                    ));
                  })()}
              </tbody>
            </table>
            </div>
            
            {/* Controles de paginação */}
            {(() => {
              const filtered = priceTablesDoEmitenteConfiguracao
                .filter((pt: any) =>
                  priceTableSearch === "" ||
                  pt.nome?.toLowerCase().includes(priceTableSearch.toLowerCase())
                );
              const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              
              if (totalPages <= 1) return null;
              
              return (
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t bg-muted/30">
                  <div className="text-sm text-muted-foreground">
                    Página {priceTablePage} de {totalPages} ({filtered.length} tabelas)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPriceTablePage(p => Math.max(1, p - 1))}
                      disabled={priceTablePage === 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPriceTablePage(p => Math.min(totalPages, p + 1))}
                      disabled={priceTablePage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
     <SelectPaymentTermModal
      open={isPaymentTermModalOpen}
      onClose={() => setIsPaymentTermModalOpen(false)}
      onSelect={(paymentTerm) => setSelectedPaymentTerm(paymentTerm)}
      emitenteId={emitenteIdConfiguracao || undefined}
    />

    {/* Modal de Seleção de Transportadora */}
    <Dialog open={isTransportadoraModalOpen} onOpenChange={setIsTransportadoraModalOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Transportadora</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Buscar transportadora..."
            value={transportadoraSearch}
            onChange={(e) => setTransportadoraSearch(e.target.value)}
          />
          <div className="border rounded-lg">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full border-collapse" style={{minWidth: '600px'}}>
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="border-b p-3 text-left" style={{minWidth: '200px'}}>Razão Social</th>
                    <th className="border-b p-3 text-left" style={{minWidth: '150px'}}>Nome Fantasia</th>
                    <th className="border-b p-3 text-left" style={{minWidth: '140px'}}>CNPJ</th>
                    <th className="border-b p-3 text-center" style={{minWidth: '110px'}}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const list = Array.isArray(transportadorasList) ? transportadorasList : [];
                    const filtered = list.filter((t: any) =>
                      transportadoraSearch === "" ||
                      (t.razaoSocial ?? '').toLowerCase().includes(transportadoraSearch.toLowerCase()) ||
                      (t.nomeFantasia ?? '').toLowerCase().includes(transportadoraSearch.toLowerCase()) ||
                      (t.cnpj ?? '').includes(transportadoraSearch)
                    );
                    return filtered.map((t: any) => (
                      <tr key={t.id} className="hover:bg-muted/50">
                        <td className="border-b p-3">{t.razaoSocial}</td>
                        <td className="border-b p-3">{t.nomeFantasia || '-'}</td>
                        <td className="border-b p-3 whitespace-nowrap">{t.cnpj || '-'}</td>
                        <td className="border-b p-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              setTransportadoraId(t.id);
                              setTransportadora(t.razaoSocial);
                              setIsTransportadoraModalOpen(false);
                              setTransportadoraSearch("");
                            }}
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            Selecionar
                          </Button>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {/* Modal de Seleção de Configuração de Orçamento */}
    <Dialog open={isQuoteConfigModalOpen} onOpenChange={setIsQuoteConfigModalOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Configuração de Orçamento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Buscar configuração..."
            value={quoteConfigSearch}
            onChange={(e) => {
              setQuoteConfigSearch(e.target.value);
              setQuoteConfigPage(1);
            }}
          />
          <div className="border rounded-lg">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full border-collapse" style={{minWidth: "500px"}}>
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="border-b p-3 text-left" style={{minWidth: "250px"}}>Nome</th>
                    <th className="border-b p-3 text-left" style={{minWidth: "80px"}}>ID</th>
                    <th className="border-b p-3 text-center" style={{minWidth: "110px"}}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = Array.isArray(quoteConfigs) ? quoteConfigs.filter((qc: any) =>
                      quoteConfigSearch === "" ||
                      (qc.nome ?? '').toLowerCase().includes(quoteConfigSearch.toLowerCase())
                    ) : [];
                    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                    const startIndex = (quoteConfigPage - 1) * ITEMS_PER_PAGE;
                    const paginatedData = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
                    return paginatedData.map((qc: any) => (
                      <tr key={qc.id} className="hover:bg-muted/50">
                        <td className="border-b p-3">{qc.nome}</td>
                        <td className="border-b p-3">{qc.id}</td>
                        <td className="border-b p-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              const emitenteMudou = emitenteIdConfiguracao > 0 && emitenteIdConfiguracao !== Number(qc.emitenteId);
                              if (emitenteMudou && items.length > 0) {
                                toast.error("Não é possível trocar a configuração para outro emitente com itens no orçamento.");
                                return;
                              }
                              if (emitenteMudou) {
                                setDestinatarioId("");
                                setDestinatarioNome("");
                                setPriceTableId("");
                                setPriceTableNome("");
                                setSelectedPaymentTerm(null);
                                setTransportadora("");
                                setTransportadoraId(null);
                              }
                              setQuoteConfigId(String(qc.id));
                              setQuoteConfigNome(qc.nome);
                              setIsQuoteConfigModalOpen(false);
                              setQuoteConfigSearch("");
                            }}
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            Selecionar
                          </Button>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {(() => {
              const filtered = Array.isArray(quoteConfigs) ? quoteConfigs.filter((qc: any) =>
                quoteConfigSearch === "" ||
                (qc.nome ?? '').toLowerCase().includes(quoteConfigSearch.toLowerCase())
              ) : [];
              const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              if (totalPages <= 1) return null;
              return (
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t bg-muted/30">
                  <div className="text-sm text-muted-foreground">
                    Página {quoteConfigPage} de {totalPages} ({filtered.length} configurações)
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setQuoteConfigPage(p => Math.max(1, p - 1))} disabled={quoteConfigPage === 1}>Anterior</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuoteConfigPage(p => Math.min(totalPages, p + 1))} disabled={quoteConfigPage === totalPages}>Próxima</Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </Fragment>
  );
}
export default EditQuoteModal;
